/**
 * WYSIWYG-редактор админки на TipTap (React).
 *
 * В legacy это был Summernote — jQuery-плагин, которому нужны ещё jQuery и Bootstrap JS
 * (он зовёт $.fn.tooltip). В React-приложении держать всю эту связку ради одного поля
 * незачем, поэтому взята обычная React-библиотека.
 *
 * Формат данных не меняется: на входе и выходе тот же HTML, что лежит в materials.text
 * и уходит в письма. Ставится там же, где в legacy был .in_editor: новости, информация,
 * главная, страница 404, тело email-рассылки.
 *
 * Панель кнопок оформлена классами Bootstrap 3 — он уже подключён из legacy-стилей,
 * так что редактор не выбивается из вида админки.
 */

import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import TextAlign from '@tiptap/extension-text-align'
import Underline from '@tiptap/extension-underline'
import { EditorContent, useEditor, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { useEffect, type ReactNode } from 'react'

interface HtmlEditorProps {
  value: string
  onChange: (html: string) => void
  /** Высота поля ввода; в legacy Summernote было 300. */
  height?: number
}

export function HtmlEditor({ value, onChange, height = 300 }: HtmlEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false }),
      Image,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: value,
    onUpdate: ({ editor: instance }) => onChange(instance.getHTML()),
    editorProps: {
      // Стили вешаем на САМО редактируемое поле (div[contenteditable], .ProseMirror),
      // а не только на рамку вокруг: по умолчанию оно высотой ровно в свой текст,
      // и клик по пустому месту ниже последней строки в редактор не попадал —
      // курсор не ставился, хотя визуально это часть поля.
      // min-height: 100% растягивает поле на всю рамку (у неё высота задана явно).
      attributes: {
        style: 'min-height: 100%; padding: 10px; outline: none; box-sizing: border-box;',
      },
    },
  })

  // Значение пришло снаружи (материал догрузился с сервера) — кладём его в редактор.
  // Сравниваем с текущим HTML, иначе каждый ввод символа сбрасывал бы курсор в начало.
  useEffect(() => {
    if (editor === null) return
    if (value === editor.getHTML()) return
    editor.commands.setContent(value, { emitUpdate: false })
  }, [editor, value])

  if (editor === null) return null

  return (
    <div style={{ marginBottom: 10 }}>
      <Toolbar editor={editor} />
      {/*
        Рамка задаёт высоту и прокрутку, отступы — у самого поля ввода (editorProps выше).
        Без height: 100% на этой обёртке min-height: 100% у поля не от чего считать.
      */}
      <div
        className="in_editor"
        style={{
          height,
          overflowY: 'auto',
          border: '1px solid #ccc',
          background: '#fff',
          cursor: 'text',
        }}
        // клик по пустому низу рамки ставит курсор в конец текста, как в обычном поле
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            event.preventDefault()
            editor.chain().focus('end').run()
          }
        }}
      >
        <EditorContent editor={editor} style={{ height: '100%' }} />
      </div>
    </div>
  )
}

function Toolbar({ editor }: { editor: Editor }) {
  const link = (): void => {
    const previous = editor.getAttributes('link').href as string | undefined
    const url = window.prompt('Адрес ссылки', previous ?? 'https://')
    if (url === null) return

    if (url.trim() === '') {
      editor.chain().focus().unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run()
  }

  const image = (): void => {
    const url = window.prompt('Адрес картинки', 'https://')
    if (url === null || url.trim() === '') return
    editor.chain().focus().setImage({ src: url.trim() }).run()
  }

  return (
    <div
      className="btn-toolbar"
      style={{ border: '1px solid #ccc', borderBottom: 0, padding: 4, background: '#f5f5f5', margin: 0 }}
    >
      <Button editor={editor} title="Полужирный" active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}>
        <b>Ж</b>
      </Button>
      <Button editor={editor} title="Курсив" active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}>
        <i>К</i>
      </Button>
      <Button editor={editor} title="Подчёркнутый" active={editor.isActive('underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}>
        <u>Ч</u>
      </Button>
      <Button editor={editor} title="Зачёркнутый" active={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}>
        <s>З</s>
      </Button>

      <Gap />

      <Button editor={editor} title="Заголовок" active={editor.isActive('heading', { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
        H2
      </Button>
      <Button editor={editor} title="Подзаголовок" active={editor.isActive('heading', { level: 3 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
        H3
      </Button>
      <Button editor={editor} title="Обычный текст" active={editor.isActive('paragraph')}
        onClick={() => editor.chain().focus().setParagraph().run()}>
        ¶
      </Button>

      <Gap />

      <Button editor={editor} title="Маркированный список" active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}>
        • —
      </Button>
      <Button editor={editor} title="Нумерованный список" active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}>
        1.
      </Button>
      <Button editor={editor} title="Цитата" active={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}>
        ❝
      </Button>

      <Gap />

      <Button editor={editor} title="По левому краю" active={editor.isActive({ textAlign: 'left' })}
        onClick={() => editor.chain().focus().setTextAlign('left').run()}>
        ⇤
      </Button>
      <Button editor={editor} title="По центру" active={editor.isActive({ textAlign: 'center' })}
        onClick={() => editor.chain().focus().setTextAlign('center').run()}>
        ↔
      </Button>
      <Button editor={editor} title="По правому краю" active={editor.isActive({ textAlign: 'right' })}
        onClick={() => editor.chain().focus().setTextAlign('right').run()}>
        ⇥
      </Button>

      <Gap />

      <Button editor={editor} title="Ссылка" active={editor.isActive('link')} onClick={link}>
        Ссылка
      </Button>
      <Button editor={editor} title="Картинка по адресу" active={false} onClick={image}>
        Картинка
      </Button>
      <Button
        editor={editor}
        title="Убрать форматирование"
        active={false}
        onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
      >
        ✕
      </Button>
    </div>
  )
}

function Button({
  title,
  active,
  onClick,
  children,
}: {
  editor: Editor
  title: string
  active: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      title={title}
      className={`btn btn-default btn-sm${active ? ' active' : ''}`}
      // mousedown, а не click: click сначала снял бы выделение в поле
      onMouseDown={(event) => {
        event.preventDefault()
        onClick()
      }}
    >
      {children}
    </button>
  )
}

function Gap() {
  return <span style={{ display: 'inline-block', width: 8 }} />
}
